import React, { useContext } from 'react';
import * as R from 'ramda';

import { Collapse, List } from '@atom/mui';
import { CategoryPathItem } from '@atom/types/category';

import { isAsset } from './InventoryAssetTree';
import LoadAssetButton from './LoadAssetsButton';
import TreeContext, { Tree } from './TreeContext';
import TreeItem from './TreeItem';

export interface Props {
  isAncestorSelected?: boolean;
  level: number;
  trees: Tree[];
  path: CategoryPathItem[];
}

const getLoadAssetsButtonStyle = (hasChildren: boolean, level: number) =>
  hasChildren
    ? { paddingLeft: `${level + 3}rem` }
    : { paddingLeft: `${level + 5}rem` };

const TreeList = ({ isAncestorSelected, level, trees, path }: Props) => {
  const {
    fetchMore,
    getData,
    hasError,
    hasMore,
    includeAssets,
    isLoading,
    open,
    selectedCategoryIds,
  } = useContext(TreeContext);

  return (
    <List disablePadding>
      {trees.map(tree => {
        const hasAssets = !isAsset(tree) && tree.hasAssets;
        const hasCategories = !isAsset(tree) && !!tree.categories?.length;
        const hasChildren = (includeAssets && hasAssets) || hasCategories;

        return (
          <React.Fragment key={tree.id}>
            <TreeItem
              hasChildren={hasChildren}
              hasCategories={hasCategories}
              isAncestorSelected={isAncestorSelected}
              level={level}
              tree={tree}
              path={[...path, { id: tree.id, name: tree.name }]}
            />
            {hasChildren && (
              <Collapse in={open.has(tree.id)} timeout="auto" unmountOnExit>
                {hasAssets ? (
                  <>
                    {
                      <TreeList
                        level={R.inc(level)}
                        trees={getData(tree.id)}
                        path={[...path, { id: tree.id, name: tree.name }]}
                      />
                    }
                    {hasMore(tree.id) && (
                      <LoadAssetButton
                        loading={isLoading(tree.id)}
                        error={hasError(tree.id)}
                        onClick={() => fetchMore(tree.id)}
                        style={getLoadAssetsButtonStyle(hasChildren, level)}
                      />
                    )}
                  </>
                ) : (
                  <TreeList
                    isAncestorSelected={
                      selectedCategoryIds?.has(tree.id) || isAncestorSelected
                    }
                    level={R.inc(level)}
                    trees={(tree as any).categories}
                    path={[...path, { id: tree.id, name: tree.name }]}
                  />
                )}
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default TreeList;
